import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import {
  UserCircleIcon,
  VideoCameraIcon,
  CurrencyDollarIcon,
  CheckIcon,
  ArrowRightIcon,
  DevicePhoneMobileIcon,
  SparklesIcon,
  GlobeAltIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import ImageUpload from '../components/ImageUpload';
import Logo from '../components/Logo';
import PricingHelper from '../components/onboarding/PricingHelper';
import { TalentCategory } from '../types';
import { uploadVideoToWasabi } from '../services/videoUpload';
import toast from 'react-hot-toast';

const STEPS = [
  { id: 1, name: 'Create Account', description: 'Sign up or login', icon: UserCircleIcon },
  { id: 2, name: 'Profile & Categories', description: 'Your public profile', icon: StarIcon },
  { id: 3, name: 'Promo Video', description: 'Introduce yourself', icon: VideoCameraIcon },
  { id: 4, name: 'Your Bio Link', description: 'Customize your link', icon: GlobeAltIcon },
];

const CATEGORY_OPTIONS: { value: TalentCategory; label: string }[] = [
  { value: 'politician', label: 'Politician' },
  { value: 'candidate', label: 'Candidate' },
  { value: 'party-leader', label: 'Party Leader' },
  { value: 'reporter', label: 'Reporter' },
  { value: 'tv-host', label: 'TV Host' },
  { value: 'commentator', label: 'Commentator' },
  { value: 'author', label: 'Author' },
  { value: 'comedian', label: 'Comedian' },
  { value: 'musician', label: 'Musician' },
  { value: 'actor', label: 'Actor' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'activist', label: 'Activist' },
  { value: 'faith-leader', label: 'Faith Leader' },
  { value: 'academic', label: 'Academic' },
  { value: 'military', label: 'Military' },
  { value: 'youth-leader', label: 'Youth Leader' },
  { value: 'patriotic-entertainer', label: 'Patriotic Entertainer' },
  { value: 'other', label: 'Other' },
];

const bioCreatorBios = [
  { name: 'Chris Ripa', handle: 'chrisripa' },
  { name: 'Greg On Fire', handle: 'gregonfire' },
  { name: 'Shawn Farash', handle: 'shawnfarash' },
  { name: 'Melonie Mac', handle: 'meloniemac' },
  { name: 'Nick Di Paolo', handle: 'nickdipaolo' },
  { name: 'Hayley Caronia', handle: 'hayleycaronia' },
  { name: 'Lydia Shaffer', handle: 'lydiashaffer' },
  { name: 'Kristin Sokoloff', handle: 'kristinsokoloff' },
];

// Safari Private mode blocks localStorage writes and throws QuotaExceededError.
// This helper falls back to sessionStorage silently so the flow never crashes.
const safeStorage = {
  getItem: (key: string): string | null => {
    try { return localStorage.getItem(key); } catch {
      try { return sessionStorage.getItem(key); } catch { return null; }
    }
  },
  setItem: (key: string, value: string): void => {
    try { localStorage.setItem(key, value); } catch {
      try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
    }
  },
  removeItem: (key: string): void => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
  },
};

const StartPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Tolerate malformed URLs like "&?ref=foo" where the extra "?" makes the
  // query key literally "?ref" instead of "ref".
  const partnerRef = (searchParams.get('ref') || searchParams.get('?ref') || '').trim();
  const adminToken = (searchParams.get('token') || searchParams.get('?token') || '').trim();
  const [currentStep, setCurrentStep] = useState(0);
  const [skipPromoVideo, setSkipPromoVideo] = useState(!!partnerRef);
  const [loading, setLoading] = useState(false);
  const [referrer, setReferrer] = useState<{ name: string; avatar: string | null; username: string | null } | null>(null);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [defaultAdminFee, setDefaultAdminFee] = useState(25);

  // OTP Auth State (same flow as admin onboarding)
  const [authStep, setAuthStep] = useState<'email' | 'phone' | 'otp'>('email');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [existingUserPhone, setExistingUserPhone] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  const otpInputRef = useRef<HTMLInputElement>(null);

  const [userId, setUserId] = useState<string | null>(null);
  const [talentProfileId, setTalentProfileId] = useState<string | null>(null);

  // Intro carousel state
  const [featuredTalent, setFeaturedTalent] = useState<Array<{ name: string; avatar: string; handle: string }>>([]);
  const introCarouselRef = useRef<HTMLDivElement>(null);

  // Profile Data
  const [profileData, setProfileData] = useState({
    fullName: '',
    username: '',
    category: '' as TalentCategory,
    categories: [] as TalentCategory[],
    bio: '',
    pricing: 50,
    instagramFollowers: 0,
    fulfillmentTime: 72,
    avatarUrl: '',
  });
  const [selectedShoutoutTypes, setSelectedShoutoutTypes] = useState<string[]>([]);

  // Multi-select dropdown open states
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [occasionDropdownOpen, setOccasionDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const occasionDropdownRef = useRef<HTMLDivElement>(null);

  // Bio link carousel
  const bioLinkCarouselRef = useRef<HTMLDivElement>(null);

  // Video
  const [promoVideo, setPromoVideo] = useState<File | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);

  useEffect(() => {
    fetchPlatformSettings();
    loadSavedProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OTP Cooldown timer
  useEffect(() => {
    if (otpCooldown > 0) {
      const timer = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCooldown]);

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user && currentStep <= 1) {
        handleUserAuthenticated(session.user.id);
      }
    };
    checkExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch featured talent for intro carousel
  useEffect(() => {
    const fetchFeatured = async () => {
      const { data } = await supabase
        .from('talent_profiles')
        .select('full_name, username, temp_avatar_url')
        .eq('is_active', true)
        .not('temp_avatar_url', 'is', null)
        .neq('temp_avatar_url', '')
        .order('is_featured', { ascending: false })
        .order('total_orders', { ascending: false })
        .limit(16);

      if (data) {
        setFeaturedTalent(
          data
            .map((t) => ({
              name: t.full_name || t.username || '',
              avatar: t.temp_avatar_url || '',
              handle: t.username || '',
            }))
            .filter((t) => t.avatar && t.name)
        );
      }
    };
    fetchFeatured();
  }, []);

  // Fetch referring partner's talent profile (avatar + name) for "Join {name} on ShoutOut." header
  useEffect(() => {
    const refToken = partnerRef || (typeof window !== 'undefined' ? safeStorage.getItem('partner_ref') : null);
    if (!refToken) return;

    const fetchReferrer = async () => {
      // One SECURITY DEFINER RPC that accepts either the partner's referral_token
      // or the partner's linked talent username. Callable by anon visitors so
      // logged-out new users see the referrer header immediately.
      const { data, error } = await supabase.rpc('get_partner_referrer_display', { p_token: refToken });
      if (error || !data) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return;
      setReferrer({
        name: row.name || '',
        avatar: row.avatar_url || null,
        username: row.username || null,
      });
    };
    fetchReferrer().catch(() => {});
  }, [partnerRef]);

  // Auto-scroll intro carousel
  useEffect(() => {
    if (currentStep !== 0) return;
    const carousel = introCarouselRef.current;
    if (!carousel || featuredTalent.length === 0) return;

    let animationId: number;
    let position = 0;
    const speed = 0.5;

    const animate = () => {
      position -= speed;
      const itemWidth = 88 + 16;
      if (Math.abs(position) >= itemWidth * featuredTalent.length) position = 0;
      carousel.style.transform = `translateX(${position}px)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [currentStep, featuredTalent]);

  // Auto-scroll bio link carousel (step 4)
  useEffect(() => {
    if (currentStep !== 4) return;
    const carousel = bioLinkCarouselRef.current;
    if (!carousel) return;

    let animationId: number;
    let position = 0;
    const speed = 0.4;

    const animate = () => {
      position -= speed;
      const cardWidth = 120 + 12;
      if (Math.abs(position) >= cardWidth * bioCreatorBios.length) position = 0;
      carousel.style.transform = `translateX(${position}px)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [currentStep]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) {
        setCategoryDropdownOpen(false);
      }
      if (occasionDropdownRef.current && !occasionDropdownRef.current.contains(e.target as Node)) {
        setOccasionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPlatformSettings = async () => {
    try {
      const { data } = await supabase.from('app_settings').select('global_admin_fee_percentage').single();
      if (data?.global_admin_fee_percentage) setDefaultAdminFee(data.global_admin_fee_percentage);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const loadSavedProgress = () => {
    if (partnerRef) safeStorage.setItem('partner_ref', partnerRef);
    const saved = safeStorage.getItem('talent_start_progress');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.currentStep && parsed.currentStep > 1) setCurrentStep(parsed.currentStep);
        if (parsed.userId) setUserId(parsed.userId);
        if (parsed.talentProfileId) setTalentProfileId(parsed.talentProfileId);
        if (parsed.profileData) setProfileData({ ...profileData, ...parsed.profileData });
        if (parsed.selectedShoutoutTypes) setSelectedShoutoutTypes(parsed.selectedShoutoutTypes);
        if (parsed.currentStep > 1) toast.success('Progress restored!');
      } catch (e) {
        console.error('Failed to parse saved progress:', e);
      }
    }
  };

  // Save progress (localStorage + DB)
  useEffect(() => {
    if (userId || currentStep > 1) {
      safeStorage.setItem('talent_start_progress', JSON.stringify({
        currentStep, userId, talentProfileId, profileData, selectedShoutoutTypes,
      }));
    }
    if (talentProfileId && currentStep > 1) {
      supabase.from('talent_profiles').update({ current_onboarding_step: currentStep }).eq('id', talentProfileId).then(() => {});
    }
  }, [currentStep, userId, talentProfileId, profileData, selectedShoutoutTypes]);

  const enqueueOnboardingEmailFlow = async (userEmail: string, uid: string) => {
    try {
      const scheduleAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      await supabase.from('user_email_flow_status').upsert({
        email: userEmail.toLowerCase(),
        user_id: uid,
        flow_id: 'aaaa5555-5555-5555-5555-555555555555',
        current_message_order: 0,
        next_email_scheduled_at: scheduleAt,
        flow_started_at: new Date().toISOString(),
        is_paused: false,
        source_url: window.location.href,
      }, { onConflict: 'email,flow_id' });
    } catch (flowErr) {
      console.warn('Could not enqueue onboarding email flow:', flowErr);
    }
  };

  // Handle authenticated user - create/get talent profile
  const handleUserAuthenticated = async (authUserId: string) => {
    setUserId(authUserId);
    setLoading(true);

    try {
      // Resolve partner referral token early so it applies to both new and existing profiles
      let partnerId: string | null = null;
      const refToken = partnerRef || safeStorage.getItem('partner_ref');
      if (refToken) {
        safeStorage.setItem('partner_ref', refToken);
        const { data: pid } = await supabase.rpc('resolve_partner_token', { p_token: refToken });
        if (pid) partnerId = pid;
      }

      // If admin onboarding token is present, link this user to the admin-created profile
      const onboardToken = adminToken || safeStorage.getItem('admin_onboard_token');
      if (onboardToken) {
        safeStorage.setItem('admin_onboard_token', onboardToken);

        const { data: adminProfile } = await supabase
          .from('talent_profiles')
          .select('id, temp_full_name, temp_avatar_url, username, bio, pricing, category, categories, selected_shoutout_types, fulfillment_time_hours, onboarding_completed, user_id, skip_promo_video')
          .eq('onboarding_token', onboardToken)
          .maybeSingle();

        if (adminProfile) {
          if (adminProfile.onboarding_completed) {
            safeStorage.removeItem('admin_onboard_token');
            toast.success('Welcome back!');
            navigate('/dashboard');
            return;
          }

          // Already linked to this user — just resume
          if (adminProfile.user_id === authUserId) {
            setTalentProfileId(adminProfile.id);
            if (adminProfile.skip_promo_video) setSkipPromoVideo(true);
            if (adminProfile.temp_full_name) setProfileData((prev) => ({ ...prev, fullName: adminProfile.temp_full_name || '' }));
            if (adminProfile.temp_avatar_url) setProfileData((prev) => ({ ...prev, avatarUrl: adminProfile.temp_avatar_url || '' }));
            if (adminProfile.username) setProfileData((prev) => ({ ...prev, username: adminProfile.username || '' }));
            if (adminProfile.bio) setProfileData((prev) => ({ ...prev, bio: adminProfile.bio || '' }));
            if (adminProfile.pricing) setProfileData((prev) => ({ ...prev, pricing: adminProfile.pricing }));
            if (adminProfile.categories?.length) setProfileData((prev) => ({ ...prev, categories: adminProfile.categories }));
            if (adminProfile.selected_shoutout_types?.length) setSelectedShoutoutTypes(adminProfile.selected_shoutout_types);
            await enqueueOnboardingEmailFlow(email, authUserId);
            toast.success('Continuing your setup...');
            setCurrentStep(2);
            return;
          }

          // Link the admin-created profile to this user via RPC
          const normalizedEmail = email.toLowerCase().trim();
          const formattedPhone = `+1${phone.replace(/\D/g, '')}`;
          const { data: linkResult } = await supabase.rpc('link_talent_profile_to_user', {
            p_talent_id: adminProfile.id,
            p_user_id: authUserId,
            p_onboarding_token: onboardToken,
            p_full_name: adminProfile.temp_full_name || null,
            p_email: normalizedEmail || null,
            p_phone: formattedPhone !== '+1' ? formattedPhone : null,
            p_avatar_url: adminProfile.temp_avatar_url || null,
          });

          if (linkResult?.success === false) {
            console.error('RPC link failed:', linkResult.error);
            // Fallback: direct update matching both id and token
            await supabase
              .from('talent_profiles')
              .update({ user_id: authUserId })
              .eq('id', adminProfile.id)
              .eq('onboarding_token', onboardToken);
          }

          setTalentProfileId(adminProfile.id);
          if (adminProfile.skip_promo_video) setSkipPromoVideo(true);
          if (adminProfile.temp_full_name) setProfileData((prev) => ({ ...prev, fullName: adminProfile.temp_full_name || '' }));
          if (adminProfile.temp_avatar_url) setProfileData((prev) => ({ ...prev, avatarUrl: adminProfile.temp_avatar_url || '' }));
          if (adminProfile.username) setProfileData((prev) => ({ ...prev, username: adminProfile.username || '' }));
          if (adminProfile.bio) setProfileData((prev) => ({ ...prev, bio: adminProfile.bio || '' }));
          if (adminProfile.pricing) setProfileData((prev) => ({ ...prev, pricing: adminProfile.pricing }));
          if (adminProfile.categories?.length) setProfileData((prev) => ({ ...prev, categories: adminProfile.categories }));
          if (adminProfile.selected_shoutout_types?.length) setSelectedShoutoutTypes(adminProfile.selected_shoutout_types);
          if (partnerId) {
            await supabase.from('talent_profiles').update({ referred_by_partner_id: partnerId }).eq('id', adminProfile.id);
          }

          await enqueueOnboardingEmailFlow(email, authUserId);
          toast.success('Account verified! Let\'s set up your profile.');
          setCurrentStep(2);
          return;
        }
        // Token not found — clear it and fall through to normal flow
        safeStorage.removeItem('admin_onboard_token');
      }

      // Check for existing talent profile
      const { data: existingProfile } = await supabase
        .from('talent_profiles')
        .select('id, onboarding_completed, referred_by_partner_id')
        .eq('user_id', authUserId)
        .maybeSingle();

      if (existingProfile?.onboarding_completed) {
        toast.success('Welcome back!');
        navigate('/dashboard');
        return;
      }

      if (existingProfile) {
        setTalentProfileId(existingProfile.id);
        // Link to partner if not already linked
        if (partnerId && !existingProfile.referred_by_partner_id) {
          await supabase.from('talent_profiles').update({ referred_by_partner_id: partnerId }).eq('id', existingProfile.id);
        }
        await enqueueOnboardingEmailFlow(email, authUserId);
        // Check for saved progress and restore step
        const saved = safeStorage.getItem('talent_start_progress');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (parsed.currentStep && parsed.currentStep > 1) {
              setCurrentStep(parsed.currentStep);
              toast.success('Continuing your setup...');
              return;
            }
          } catch (e) { /* ignore */ }
        }
        // Default to step 2 if no saved progress
        toast.success('Continuing your setup...');
        setCurrentStep(2);
        return;
      }

      // Get user data
      const { data: userData } = await supabase
        .from('users')
        .select('full_name, email')
        .eq('id', authUserId)
        .single();

      // Create new talent profile
      const insertData: any = {
        user_id: authUserId,
        category: 'other',
        bio: '',
        pricing: 50,
        fulfillment_time_hours: 72,
        is_featured: false,
        is_active: false,
        total_orders: 0,
        fulfilled_orders: 0,
        average_rating: 0,
        admin_fee_percentage: defaultAdminFee,
        first_orders_promo_active: true,
        onboarding_completed: false,
        current_onboarding_step: 2,
      };
      if (partnerId) insertData.referred_by_partner_id = partnerId;

      const { data: newProfile, error: profileError } = await supabase
        .from('talent_profiles')
        .insert(insertData)
        .select()
        .single();

      if (profileError) {
        console.error('Profile creation error:', profileError);
        toast.error(`Failed to create profile: ${profileError.message || 'RLS policy may be blocking. Contact support.'}`);
        return;
      }

      setTalentProfileId(newProfile.id);
      if (userData?.full_name) {
        setProfileData((prev) => ({ ...prev, fullName: userData.full_name || '' }));
      }

      await enqueueOnboardingEmailFlow(email, authUserId);

      toast.success('Account verified! Let\'s set up your profile.');
      setCurrentStep(2);
    } catch (error: any) {
      console.error('Error setting up profile:', error);
      toast.error(error.message || 'Failed to set up profile');
    } finally {
      setLoading(false);
    }
  };

  // Username check
  useEffect(() => {
    if (!profileData.username || profileData.username.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    setCheckingUsername(true);
    const timeout = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from('talent_profiles')
          .select('id')
          .eq('username', profileData.username.toLowerCase())
          .neq('id', talentProfileId || '00000000-0000-0000-0000-000000000000')
          .maybeSingle();
        setUsernameAvailable(!data);
      } catch (e) {
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [profileData.username, talentProfileId]);

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedEmail = email.toLowerCase().trim();
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: normalizedEmail, checkEmailOnly: true },
      });

      if (error) {
        console.error('Email check error:', error);
        setAuthStep('phone');
        return;
      }

      if (data?.sentToExistingPhone && data?.phone) {
        setExistingUserPhone(data.phone);
        setPhone(data.phone);
        toast.success('We sent a code to your phone!');
        setAuthStep('otp');
        setOtpCooldown(60);
        setTimeout(() => otpInputRef.current?.focus(), 100);
      } else {
        setAuthStep('phone');
      }
    } catch (error: any) {
      console.error('Email check error:', error);
      setAuthStep('phone');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    const formattedPhone = `+1${digits}`;
    const normalizedEmail = email.toLowerCase().trim();

    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: normalizedEmail, phone: formattedPhone },
      });

      if (error) throw new Error(error.message || 'Failed to send verification code');
      if (data?.error) throw new Error(data.error);

      toast.success('Verification code sent!');
      setAuthStep('otp');
      setOtpCooldown(60);
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch (error: any) {
      console.error('Send OTP error:', error);
      toast.error(error.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (otpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    const normalizedEmail = email.toLowerCase().trim();
    const formattedPhone = existingUserPhone || `+1${phone.replace(/\D/g, '')}`;

    try {
      const { data, error } = await supabase.functions.invoke('verify-registration-otp', {
        body: { email: normalizedEmail, phone: formattedPhone, code: otpCode },
      });

      if (error) throw new Error(error.message || 'Invalid verification code');
      if (data?.error) throw new Error(data.error);

      if (data?.session?.access_token && data?.session?.refresh_token) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });

        if (sessionError) {
          console.error('Session error:', sessionError);
          throw new Error('Failed to establish session');
        }
      } else {
        throw new Error('No session returned from verification');
      }

      // Create or update user record to be a talent
      const { error: userInsertError } = await supabase.from('users').insert({
        id: data.user.id,
        email: normalizedEmail,
        phone: formattedPhone,
        user_type: 'talent',
        created_at: new Date().toISOString(),
      });

      if (userInsertError) {
        const { error: userUpdateError } = await supabase.from('users').update({
          user_type: 'talent',
          phone: formattedPhone,
        }).eq('id', data.user.id);
        if (userUpdateError) console.error('Failed to update user type:', userUpdateError);
      }

      toast.success(data.isLogin ? 'Welcome back!' : 'Account verified!');
      await handleUserAuthenticated(data.user.id);
    } catch (error: any) {
      console.error('OTP verification error:', error);
      toast.error(error.message || 'Invalid verification code');
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpCooldown > 0) return;

    setLoading(true);
    const normalizedEmail = email.toLowerCase().trim();
    const formattedPhone = existingUserPhone || `+1${phone.replace(/\D/g, '')}`;

    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: normalizedEmail, phone: formattedPhone },
      });
      if (error) throw new Error(error.message || 'Failed to resend code');
      if (data?.error) throw new Error(data.error);

      toast.success('New code sent!');
      setOtpCooldown(60);
      setOtpCode('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!talentProfileId) return toast.error('Profile not found');
    if (!profileData.fullName) return toast.error('Full name required');
    if (!profileData.username) return toast.error('Username required');
    if (usernameAvailable === false) return toast.error('Username taken');
    if (!profileData.bio || profileData.bio.length < 50) return toast.error('Bio must be 50+ characters');
    if (profileData.categories.length === 0) return toast.error('Select at least one category');
    setLoading(true);
    try {
      const { error } = await supabase
        .from('talent_profiles')
        .update({
          full_name: profileData.fullName,
          temp_full_name: profileData.fullName,
          username: profileData.username.toLowerCase(),
          bio: profileData.bio,
          pricing: profileData.pricing,
          fulfillment_time_hours: profileData.fulfillmentTime,
          temp_avatar_url: profileData.avatarUrl,
          category: profileData.categories[0],
          categories: profileData.categories,
          selected_shoutout_types: selectedShoutoutTypes,
        })
        .eq('id', talentProfileId);

      if (error) throw error;

      if (userId) {
        await supabase.from('users').update({
          full_name: profileData.fullName,
          avatar_url: profileData.avatarUrl || undefined,
        }).eq('id', userId);
      }
      toast.success('Profile saved!');
      if (skipPromoVideo) {
        await supabase.from('talent_profiles').update({ is_active: true }).eq('id', talentProfileId);
        setCurrentStep(4);
      } else {
        setCurrentStep(3);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promoVideo) return toast.error('Please upload a video');
    if (!talentProfileId) return toast.error('Profile not found. Please refresh and try again.');

    console.log('[video] submit start', { size: promoVideo.size, talentProfileId });
    setVideoUploading(true);
    try {
      const result = await uploadVideoToWasabi(promoVideo, talentProfileId);
      console.log('[video] upload result', result);
      if (!result.success || !result.videoUrl) throw new Error(result.error || 'Video upload failed');

      console.log('[video] updating talent_profiles row...');
      const { error: updateError } = await supabase
        .from('talent_profiles')
        .update({ promo_video_url: result.videoUrl, is_active: true })
        .eq('id', talentProfileId);

      if (updateError) {
        console.error('[video] talent_profiles update error', updateError);
        throw updateError;
      }
      console.log('[video] talent_profiles updated');

      // Complete the talent_signup email flow (stop sending reminders) - non-blocking
      (async () => {
        try {
          const now = new Date().toISOString();
          await supabase.from('user_email_flow_status')
            .update({ flow_completed_at: now, updated_at: now })
            .eq('email', email.toLowerCase())
            .eq('flow_id', 'aaaa5555-5555-5555-5555-555555555555')
            .is('flow_completed_at', null);
        } catch (flowErr) {
          console.warn('[video] could not complete onboarding email flow:', flowErr);
        }
      })();

      toast.success('Video uploaded!');
      setCurrentStep(4);
    } catch (error: any) {
      console.error('[video] submit error', error);
      toast.error(error?.message || 'Upload failed');
    } finally {
      setVideoUploading(false);
    }
  };

  const handleBioLinkComplete = async () => {
    if (!talentProfileId) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('talent_profiles')
        .update({ onboarding_completed: true, is_active: true })
        .eq('id', talentProfileId);

      if (error) throw error;

      try {
        await supabase.functions.invoke('onboarding-complete-notification', {
          body: { talentId: talentProfileId, talentName: profileData.fullName, email: email },
        });
      } catch (e) {
        console.warn('Failed to send admin notification:', e);
      }

      // Send welcome/completion email with their ShoutOut Fans URL
      try {
        const firstName = (profileData.fullName || '').split(' ')[0] || 'there';
        const fanLink = `https://shoutout.fans/${profileData.username}`;
        const dashboardLink = `https://creator.shoutout.us/dashboard?token=${talentProfileId}&email=${encodeURIComponent(email)}&userId=${userId}`;
        const logoUrl = 'https://utafetamgwukkbrlezev.supabase.co/storage/v1/object/public/platform-assets/logos/logowhite.png';

        const welcomeHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;background-color:#0f172a;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#0f172a;">
<tr><td align="center" style="padding:40px 20px;">
<table role="presentation" width="600" cellspacing="0" cellpadding="0" style="max-width:600px;width:100%;">
<tr><td align="center" style="padding-bottom:30px;"><a href="https://shoutout.us" style="text-decoration:none;"><img src="${logoUrl}" alt="ShoutOut" width="150" style="display:block;"></a></td></tr>
<tr><td style="background:linear-gradient(135deg,#1e293b 0%,#0f172a 100%);border-radius:16px;border:1px solid rgba(255,255,255,0.1);padding:40px;">
<h1 style="margin:0 0 24px 0;font-size:28px;font-weight:bold;color:#ffffff;">You're live, ${firstName}! 🎉</h1>
<p style="margin:0 0 20px 0;font-size:16px;line-height:1.6;color:#cbd5e1;">Your ShoutOut Fans link is ready. Share it everywhere — your bio, your posts, your stories.</p>
<div style="background:linear-gradient(135deg,rgba(16,185,129,0.15),rgba(6,182,212,0.15));border:1px solid rgba(16,185,129,0.3);border-radius:12px;padding:20px;margin:24px 0;text-align:center;">
<p style="margin:0 0 6px 0;font-size:13px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;">Your Link</p>
<a href="${fanLink}" style="font-size:22px;font-weight:800;color:#10b981;text-decoration:none;">shoutout.fans/${profileData.username}</a>
</div>
<p style="margin:0 0 16px 0;font-size:16px;line-height:1.6;color:#cbd5e1;">Here's how to get the most out of it:</p>
<table role="presentation" cellspacing="0" cellpadding="0" width="100%" style="margin:16px 0;">
<tr><td style="padding:8px 0;"><table role="presentation" cellspacing="0" cellpadding="0"><tr><td style="vertical-align:top;padding-right:12px;font-size:18px;">🔗</td><td style="vertical-align:top;"><p style="margin:0;font-size:15px;line-height:1.5;color:#cbd5e1;"><strong style="color:#ffffff;">Add your links</strong> — Rumble, podcast, YouTube, merch, anything you want fans to see</p></td></tr></table></td></tr>
<tr><td style="padding:8px 0;"><table role="presentation" cellspacing="0" cellpadding="0"><tr><td style="vertical-align:top;padding-right:12px;font-size:18px;">💰</td><td style="vertical-align:top;"><p style="margin:0;font-size:15px;line-height:1.5;color:#cbd5e1;"><strong style="color:#ffffff;">Add a sponsor</strong> — Pre-paid, pre-vetted sponsors earn you $60–$120 per 100 clicks. Takes 30 seconds.</p></td></tr></table></td></tr>
<tr><td style="padding:8px 0;"><table role="presentation" cellspacing="0" cellpadding="0"><tr><td style="vertical-align:top;padding-right:12px;font-size:18px;">📱</td><td style="vertical-align:top;"><p style="margin:0;font-size:15px;line-height:1.5;color:#cbd5e1;"><strong style="color:#ffffff;">Replace Linktree</strong> — Use your shoutout.fans link as your link in bio on Instagram, X, and everywhere else</p></td></tr></table></td></tr>
</table>
<table role="presentation" cellspacing="0" cellpadding="0" style="margin:30px 0;"><tr><td style="background:linear-gradient(135deg,#7c3aed 0%,#a855f7 100%);border-radius:12px;"><a href="${dashboardLink}" style="display:inline-block;padding:16px 32px;font-size:16px;font-weight:bold;color:#ffffff;text-decoration:none;">Open Your Dashboard &rarr;</a></td></tr></table>
<p style="margin:0 0 0 0;font-size:13px;color:#64748b;">Tip: The more links and content on your page, the more your fans engage.</p>
<p style="margin:30px 0 0 0;font-size:14px;color:#94a3b8;">Welcome to ShoutOut,<br>The ShoutOut Team</p>
</td></tr>
<tr><td style="padding-top:32px;text-align:center;"><div style="border-top:1px solid rgba(255,255,255,0.1);padding-top:24px;">
<a href="https://shoutout.us" style="text-decoration:none;display:inline-block;margin-bottom:16px;"><img src="${logoUrl}" alt="ShoutOut" width="100" style="display:block;opacity:0.6;"></a>
<p style="color:#64748b;font-size:12px;margin:0 0 16px 0;"><a href="https://shoutout.us/privacy" style="color:#64748b;text-decoration:underline;">Privacy Policy</a></p>
<p style="color:#475569;font-size:11px;margin:0;">ShoutOut, LLC &bull; 1201 N Riverfront Blvd Ste 100, Dallas, TX 75207</p>
</div></td></tr>
</table></td></tr></table></body></html>`;

        await supabase.functions.invoke('send-mailgun', {
          body: {
            to: email,
            subject: `You're live! Here's your ShoutOut Fans link`,
            html: welcomeHtml,
            from: 'ShoutOut <noreply@mail.shoutout.us>',
          },
        });
      } catch (emailErr) {
        console.warn('Could not send welcome email:', emailErr);
      }

      safeStorage.removeItem('talent_start_progress');
      safeStorage.removeItem('talent_start_email');
      safeStorage.removeItem('admin_onboard_token');

      toast.success('🎉 Welcome to ShoutOut! Setting up your dashboard...');
      setTimeout(() => {
        // Relative redirect — we're already on creator.shoutout.us.
        window.location.href = `/dashboard?token=${talentProfileId}&email=${encodeURIComponent(email)}&userId=${userId}&tab=customize`;
      }, 1500);
    } catch (error: any) {
      toast.error(error.message || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#0f172a] to-slate-900">
      {/* Header */}
      <div className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <a href="https://shoutout.us">
              <Logo size="sm" theme="dark" />
            </a>
            {referrer && (
              <div className="flex items-center gap-2 sm:gap-3">
                {referrer.avatar ? (
                  <img
                    src={referrer.avatar}
                    alt={referrer.name}
                    className="h-9 w-9 sm:h-11 sm:w-11 rounded-full object-cover ring-2 ring-white/20"
                  />
                ) : (
                  <div className="h-9 w-9 sm:h-11 sm:w-11 rounded-full bg-white/10 ring-2 ring-white/20 flex items-center justify-center text-white font-semibold">
                    {(referrer.name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <p className="text-xs sm:text-sm text-gray-200">
                  Join <span className="font-semibold text-white">{referrer.name}</span> on ShoutOut.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Step 0: Intro / Platform Overview */}
      {currentStep === 0 && (
        <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8 lg:py-10">
          <div className="glass rounded-2xl shadow-2xl overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10">
            <div className="p-6 sm:p-8 lg:p-10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm font-semibold mb-5">
                  <SparklesIcon className="w-4 h-4" />
                  For Creators
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
                  Transform your audience into a{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-cyan-500">monetizable, uncancellable</span>{' '}
                  engine that fuels your growth.
                </h2>
                <p className="text-lg text-gray-400">
                  Join the biggest voices in the free speech movement.
                </p>
              </div>

              {featuredTalent.length > 0 && (
                <div className="relative mb-8 -mx-6 sm:-mx-8 lg:-mx-10 overflow-hidden">
                  <div className="overflow-hidden py-3">
                    <div
                      ref={introCarouselRef}
                      className="flex gap-4"
                      style={{ width: 'max-content' }}
                    >
                      {[...featuredTalent, ...featuredTalent, ...featuredTalent, ...featuredTalent].map((talent, i) => (
                        <div key={i} className="flex flex-col items-center flex-shrink-0" style={{ width: '88px' }}>
                          <div className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-xl overflow-hidden bg-gradient-to-br from-emerald-400 to-cyan-400 p-[2px]">
                            <img
                              src={talent.avatar}
                              alt={talent.name}
                              className="w-full h-full object-cover rounded-[10px]"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          </div>
                          <p className="text-[10px] sm:text-xs text-gray-500 font-medium mt-1.5 text-center truncate w-full">{talent.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="text-center mb-8">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 text-sm font-semibold text-gray-200">
                  Ditch Linktree for the only link in bio built for free-speech creators
                </span>
              </div>

              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-xl border border-emerald-500/30">
                  <VideoCameraIcon className="w-6 h-6 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p className="font-semibold text-lg text-white">Sell personalized video shoutouts directly from your shoutout.fans link</p>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <CheckIcon className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-300">Engage followers through fan driven messages and donations</p>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <CheckIcon className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-300">Add pre-vetted, pre-paid sponsors to your shoutout.fans bio page</p>
                </div>

                <div className="flex items-center gap-3 p-4 bg-amber-500/20 rounded-xl border border-amber-500/30">
                  <CurrencyDollarIcon className="w-6 h-6 text-amber-400 flex-shrink-0" />
                  <p className="text-amber-200 font-semibold">Earn $60–$120 per 100 clicks by adding a sponsor to your link.</p>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <SparklesIcon className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-300"><span className="text-purple-400 font-medium">Coming soon:</span> sell merch like never before</p>
                </div>
              </div>

              <div className="text-center mb-8 p-4 bg-white/5 rounded-xl">
                <p className="text-lg text-white font-semibold">
                  All inside one, powerful link that averages a{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-cyan-500 font-bold">26% click through rate</span>.
                </p>
              </div>

              <button
                onClick={() => setCurrentStep(1)}
                className="w-full py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold text-lg rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25"
              >
                Get Started
                <ArrowRightIcon className="w-5 h-5" />
              </button>

              <p className="text-center text-xs text-gray-400 mt-4">
                Already have an account? Click Get Started to log in.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Wizard Steps 1-4 */}
      {currentStep >= 1 && (() => {
        const activeSteps = skipPromoVideo ? STEPS.filter((s) => s.id !== 3) : STEPS;
        const visibleIndex = activeSteps.findIndex((s) => s.id === currentStep) + 1;
        return (
          <div className="max-w-4xl mx-auto px-4 py-4 sm:py-6 lg:py-8">
            {/* Mobile Progress Bar */}
            <div className="lg:hidden mb-4">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Step {visibleIndex} of {activeSteps.length}</span>
                  <span className="text-xs text-emerald-400">{activeSteps.find((s) => s.id === currentStep)?.name}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  {activeSteps.map((step) => {
                    const isCompleted = currentStep > step.id;
                    const isCurrent = currentStep === step.id;
                    return (
                      <div
                        key={step.id}
                        className={`flex-1 h-1.5 rounded-full transition-all ${
                          isCompleted ? 'bg-emerald-500' : isCurrent ? 'bg-gradient-to-r from-emerald-500 to-cyan-500' : 'bg-white/10'
                        }`}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-[280px,1fr] gap-6 lg:gap-8">
              {/* Sidebar */}
              <div className="hidden lg:block lg:sticky lg:top-6 lg:self-start">
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4 sm:p-6">
                  <h3 className="text-base font-semibold text-white mb-4">Your Progress</h3>
                  <nav className="space-y-1.5">
                    {activeSteps.map((step) => {
                      const Icon = step.icon;
                      const isCompleted = currentStep > step.id;
                      const isCurrent = currentStep === step.id;
                      return (
                        <div
                          key={step.id}
                          className={`flex items-center gap-3 p-2.5 rounded-xl transition-all ${
                            isCurrent ? 'bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30' : ''
                          } ${isCompleted ? 'opacity-60' : ''}`}
                        >
                          <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                            isCompleted ? 'bg-emerald-500' : isCurrent ? 'bg-gradient-to-r from-emerald-500 to-cyan-500' : 'bg-white/10'
                          }`}>
                            {isCompleted ? <CheckIcon className="w-4 h-4 text-white" /> : <Icon className="w-4 h-4 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isCurrent ? 'text-white' : 'text-gray-300'}`}>{step.name}</p>
                            <p className="text-xs text-gray-500 truncate">{step.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </nav>
                  <div className="mt-4 p-3 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                    <p className="text-xs text-emerald-300">
                      <SparklesIcon className="w-4 h-4 inline mr-1" />
                      Progress auto-saved. Continue anytime!
                    </p>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="rounded-2xl shadow-2xl min-w-0 overflow-hidden" style={{ background: '#ffffff' }}>
                {/* Step 1: Email-first OTP Auth */}
                {currentStep === 1 && (
                  <div className="p-6 sm:p-8 lg:p-10">
                    <div className="max-w-md mx-auto">
                      {authStep === 'email' && (
                        <form onSubmit={handleEmailSubmit} className="space-y-5">
                          <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                              <DevicePhoneMobileIcon className="w-8 h-8 text-white" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">Get Started</h2>
                            <p className="text-gray-600 text-sm mt-1">
                              Enter your email to sign up or log in
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email Address
                            </label>
                            <input
                              type="email"
                              required
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-gray-900"
                              placeholder="Enter your email"
                              autoFocus
                            />
                          </div>

                          <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {loading ? 'Checking...' : 'Continue'}
                            <ArrowRightIcon className="w-4 h-4" />
                          </button>
                        </form>
                      )}

                      {authStep === 'phone' && (
                        <form onSubmit={handlePhoneSubmit} className="space-y-5">
                          <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Add Your Phone</h2>
                            <p className="text-gray-600 text-sm mt-1">
                              We'll send a verification code to your phone
                            </p>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-3 mb-4">
                            <p className="text-sm text-gray-600">
                              Email: <span className="font-medium">{email}</span>
                              <button
                                type="button"
                                onClick={() => setAuthStep('email')}
                                className="ml-2 text-emerald-600 hover:text-emerald-700 text-sm"
                              >
                                Change
                              </button>
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Phone Number
                            </label>
                            <input
                              type="tel"
                              required
                              value={phone}
                              onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                              className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-gray-900"
                              placeholder="(555) 123-4567"
                              autoFocus
                            />
                            <p className="text-xs text-gray-500 mt-1">We'll send you a verification code</p>
                          </div>

                          <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            <DevicePhoneMobileIcon className="w-5 h-5" />
                            {loading ? 'Sending code...' : 'Send Verification Code'}
                          </button>
                        </form>
                      )}

                      {authStep === 'otp' && (
                        <form onSubmit={handleOtpSubmit} className="space-y-5">
                          <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Enter Code</h2>
                            <p className="text-gray-600 text-sm mt-1">
                              We sent a 6-digit code to
                            </p>
                            <p className="text-emerald-600 font-medium">
                              {existingUserPhone
                                ? `***-***-${existingUserPhone.slice(-4)}`
                                : phone
                              }
                            </p>
                          </div>

                          <div>
                            <input
                              ref={otpInputRef}
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              required
                              value={otpCode}
                              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-center text-2xl tracking-[0.5em] font-mono text-gray-900"
                              placeholder="000000"
                              autoComplete="one-time-code"
                              autoFocus
                            />
                          </div>

                          <button
                            type="submit"
                            disabled={loading || otpCode.length !== 6}
                            className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {loading ? 'Verifying...' : 'Verify & Continue'}
                            <ArrowRightIcon className="w-4 h-4" />
                          </button>

                          <div className="text-center space-y-2">
                            <button
                              type="button"
                              onClick={handleResendOtp}
                              disabled={otpCooldown > 0 || loading}
                              className="text-sm text-emerald-600 hover:text-emerald-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Didn't receive the code? Resend"}
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setAuthStep('phone');
                                setOtpCode('');
                                setExistingUserPhone(null);
                              }}
                              className="block w-full text-sm text-gray-500 hover:text-gray-700"
                            >
                              Use a different phone number
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 2: Profile + Categories */}
                {currentStep === 2 && (
                  <div className="p-6 sm:p-8 lg:p-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Your ShoutOut Profile</h2>
                    <p className="text-gray-600 mb-6">For your Link In Bio and your video ShoutOut orders.</p>

                    <form onSubmit={handleProfileSubmit} className="space-y-5">
                      <div>
                        <ImageUpload
                          currentImageUrl={profileData.avatarUrl}
                          onImageUploaded={(url) => setProfileData({ ...profileData, avatarUrl: url })}
                          uploadPath="talent-avatars"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                        <input
                          type="text"
                          required
                          value={profileData.fullName}
                          onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                          placeholder="John Smith"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                        <div className="flex">
                          <span className="inline-flex items-center px-4 rounded-l-xl bg-gray-100 text-gray-500 text-sm border border-r-0 border-gray-300">
                            shoutout.us/
                          </span>
                          <input
                            type="text"
                            required
                            value={profileData.username}
                            onChange={(e) => setProfileData({ ...profileData, username: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                            className={`flex-1 px-4 py-3 border rounded-r-xl focus:ring-2 ${
                              usernameAvailable === false ? 'border-red-500 focus:ring-red-500' :
                              usernameAvailable === true ? 'border-emerald-500 focus:ring-emerald-500' :
                              'border-gray-300 focus:ring-emerald-500'
                            }`}
                            placeholder="yourname"
                          />
                        </div>
                        {usernameAvailable === false && <p className="text-xs text-red-500 mt-1">Username taken</p>}
                        {usernameAvailable === true && <p className="text-xs text-emerald-500 mt-1">✓ Available</p>}
                        {checkingUsername && <p className="text-xs text-gray-500 mt-1">Checking...</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Bio * (min 50 characters)</label>
                        <textarea
                          required
                          minLength={50}
                          rows={3}
                          value={profileData.bio}
                          onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                          placeholder="Tell your audience about yourself..."
                        />
                        <p className="text-xs text-gray-500 mt-1">{profileData.bio.length}/50 min</p>
                      </div>

                      <div ref={categoryDropdownRef} className="relative" style={{ zIndex: categoryDropdownOpen ? 50 : 'auto' }}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Categories * (up to 3)</label>
                        <button
                          type="button"
                          onClick={() => { setCategoryDropdownOpen(!categoryDropdownOpen); setOccasionDropdownOpen(false); }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 bg-white text-left flex items-center justify-between"
                        >
                          <span className={profileData.categories.length > 0 ? 'text-gray-900' : 'text-gray-400'}>
                            {profileData.categories.length > 0
                              ? profileData.categories.map((c) => CATEGORY_OPTIONS.find((o) => o.value === c)?.label).join(', ')
                              : 'Select categories...'}
                          </span>
                          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {categoryDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 rounded-xl shadow-2xl max-h-60 overflow-y-auto border border-white/20" style={{ backgroundColor: 'rgba(15, 23, 42, 0.97)', backdropFilter: 'blur(24px)' }}>
                            {CATEGORY_OPTIONS.map((opt) => {
                              const isSelected = profileData.categories.includes(opt.value);
                              const isDisabled = !isSelected && profileData.categories.length >= 3;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  disabled={isDisabled}
                                  onClick={() => {
                                    const updated = isSelected
                                      ? profileData.categories.filter((c) => c !== opt.value)
                                      : [...profileData.categories, opt.value];
                                    setProfileData({ ...profileData, categories: updated.slice(0, 3) });
                                  }}
                                  className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/10'} ${isSelected ? 'bg-emerald-500/15' : ''}`}
                                >
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-500'}`}>
                                    {isSelected && <CheckIcon className="w-3.5 h-3.5 text-white" />}
                                  </div>
                                  <span className="text-gray-200 text-sm">{opt.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <PricingHelper
                        followers={profileData.instagramFollowers}
                        onFollowersChange={(f) => setProfileData({ ...profileData, instagramFollowers: f })}
                        price={profileData.pricing}
                        onPriceChange={(p) => setProfileData({ ...profileData, pricing: p })}
                      />

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {loading ? 'Saving...' : 'Continue'}
                        <ArrowRightIcon className="w-4 h-4" />
                      </button>
                    </form>
                  </div>
                )}

                {/* Step 3: Promo Video */}
                {currentStep === 3 && (
                  <div className="p-6 sm:p-8 lg:p-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Your Promo Video</h2>
                    <p className="text-gray-600 mb-6">Introduce yourself in 30-60 seconds to fans who want a Personalized Video ShoutOut.</p>

                    <form onSubmit={handleVideoSubmit} className="space-y-5">
                      <details className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                        <summary className="font-medium text-blue-800 cursor-pointer">📝 Script Template</summary>
                        <div className="mt-3 text-sm text-blue-700 space-y-1">
                          <p><strong>1.</strong> "Hi! I'm [Your Name]..."</p>
                          <p><strong>2.</strong> "[Who you are - your background]"</p>
                          <p><strong>3.</strong> "I'm on ShoutOut - order a personalized video..."</p>
                          <p><strong>4.</strong> "[What makes your videos special]"</p>
                          <p><strong>5.</strong> "Find me on ShoutOut!"</p>
                        </div>
                      </details>

                      <div>
                        <input
                          type="file"
                          id="video-upload"
                          accept="video/*"
                          onChange={(e) => setPromoVideo(e.target.files?.[0] || null)}
                          className="hidden"
                        />
                        <label
                          htmlFor="video-upload"
                          className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-all"
                        >
                          <VideoCameraIcon className="w-8 h-8 text-gray-400" />
                          <span className="text-gray-600">{promoVideo ? promoVideo.name : 'Choose Video File'}</span>
                        </label>
                        <p className="text-xs text-gray-500 mt-2 text-center">MP4, MOV, AVI • Max 300MB</p>
                      </div>

                      {promoVideo && (
                        <div className="rounded-xl overflow-hidden bg-black">
                          <video src={URL.createObjectURL(promoVideo)} controls className="w-full" style={{ maxHeight: '300px' }} />
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={!promoVideo || videoUploading}
                        className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {videoUploading ? 'Uploading...' : 'Continue'}
                        <ArrowRightIcon className="w-4 h-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => setCurrentStep(4)}
                        className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Skip for now — you can add this later
                      </button>
                    </form>
                  </div>
                )}

                {/* Step 4: Your Bio Link */}
                {currentStep === 4 && (
                  <div className="p-6 sm:p-8 lg:p-10 overflow-hidden rounded-2xl" style={{ background: '#ffffff' }}>
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <GlobeAltIcon className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="mb-2" style={{ fontSize: '1.5rem', lineHeight: '2rem', fontWeight: 700, color: '#111827' }}>Your Link In Bio Is Ready!</h2>
                      <p style={{ color: '#4b5563' }}>
                        You now have a <span className="font-semibold" style={{ color: '#059669' }}>shoutout.fans/{profileData.username || 'yourname'}</span> page.
                        Customize it from your dashboard — add links, social accounts, and more.
                      </p>
                    </div>

                    <div className="relative overflow-hidden mb-6 rounded-xl">
                      <div className="overflow-x-clip overflow-y-visible pb-4">
                        <div
                          ref={bioLinkCarouselRef}
                          className="flex gap-3"
                          style={{ width: 'max-content' }}
                        >
                          {[...bioCreatorBios, ...bioCreatorBios, ...bioCreatorBios].map((creator, index) => (
                            <div key={index} className="flex-shrink-0 w-[120px]">
                              <div className="relative rounded-xl overflow-hidden bg-[#1a1a2e]" style={{ boxShadow: '0 15px 30px -8px rgba(0, 0, 0, 0.15)' }}>
                                <div className="relative w-full h-[220px] overflow-hidden">
                                  <iframe
                                    src={`https://shoutout.fans/${creator.handle}`}
                                    title={`${creator.name}'s bio`}
                                    className="border-0 rounded-xl origin-top-left pointer-events-none"
                                    loading="eager"
                                    scrolling="no"
                                    style={{
                                      width: '375px',
                                      height: '667px',
                                      transform: 'scale(0.32)',
                                      transformOrigin: 'top left',
                                    }}
                                  />
                                  <div className="absolute inset-0 cursor-default" />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/80 to-transparent">
                                  <p className="font-medium text-[10px] text-white text-center">{creator.name}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3 p-3 rounded-xl" style={{ background: '#ecfdf5', border: '1px solid #d1fae5' }}>
                        <CheckIcon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#047857' }} />
                        <p className="text-sm font-medium" style={{ color: '#1f2937' }}>Add your social links, YouTube, Rumble, podcast, and more</p>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-xl" style={{ background: '#ecfdf5', border: '1px solid #d1fae5' }}>
                        <CheckIcon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#047857' }} />
                        <p className="text-sm font-medium" style={{ color: '#1f2937' }}>Your ShoutOut order button is automatically included</p>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-xl" style={{ background: '#ecfdf5', border: '1px solid #d1fae5' }}>
                        <CheckIcon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#047857' }} />
                        <p className="text-sm font-medium" style={{ color: '#1f2937' }}>Add a sponsor card and earn $60–$120 per 100 clicks</p>
                      </div>
                    </div>

                    <button
                      onClick={handleBioLinkComplete}
                      disabled={loading}
                      className="w-full py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 font-bold text-lg rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25"
                      style={{ color: '#ffffff' }}
                    >
                      {loading ? 'Setting up your dashboard...' : 'Launch Your Creator Dashboard'}
                      <ArrowRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default StartPage;
